/* The lead pipe.
 *
 * Every surface that collects a name and a way to reach somebody — the
 * homepage form, the free tools' feedback modal — posts through here, so the
 * API sees one payload shape and a new surface cannot quietly invent its own.
 *
 * Endpoint and key come from Vercel env (CRA inlines REACT_APP_* at build).
 * Both fall back to the local Laravel dev server when unset.
 */

import { getAttribution } from "./attribution";

const ENDPOINT =
  process.env.REACT_APP_FORM_ENDPOINT || "http://localhost:8000/api/contact";
const API_KEY = process.env.REACT_APP_FORM_TOKEN || "";

// Strip angle brackets (basic XSS hygiene) and trim. Server sanitizes too.
export function clean(s) {
  return String(s ?? "").replace(/[<>]/g, "").trim();
}

/**
 * One box takes either a phone or an email, because asking someone standing at
 * a POS to pick the right field is a way to lose them. Tell the two apart here
 * so the payload can still name what it is holding.
 */
export function classifyContact(raw) {
  const v = clean(raw);
  if (!v) return { ok: false };
  if (v.includes("@")) {
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    return ok ? { ok: true, type: "email", value: v.toLowerCase() } : { ok: false };
  }
  const digits = v.replace(/\D/g, "");
  if (digits.length >= 10 && digits.length <= 15) {
    return { ok: true, type: "phone", value: v };
  }
  return { ok: false };
}

/**
 * POST one lead. Throws on anything but a 2xx so callers show their own error
 * state rather than reporting a send that never landed.
 *
 * @param {{name:string, contact:{type:string,value:string}, message:string,
 *          source?:string, page?:string}} lead
 */
export async function submitLead({ name, contact, message, source = "exhale-website", page }) {
  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(API_KEY && { Authorization: `Bearer ${API_KEY}` }),
    },
    body: JSON.stringify({
      name,
      contact: contact.value,
      contact_type: contact.type,
      phone: contact.type === "phone" ? contact.value : "",
      email: contact.type === "email" ? contact.value : "",
      message,
      source,
      page: page || (typeof window !== "undefined" ? window.location.pathname : ""),
      referrer: (typeof document !== "undefined" && document.referrer) || null,
      attribution: getAttribution(),
    }),
  });
  if (!res.ok) throw new Error(`lead endpoint returned ${res.status}`);
  return true;
}
